import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useRouter } from "@/app/hooks/useRouter";
import { getStaffProfileColumns } from "@/app/hooks/custom_hooks";
import DataTable from "@/app/components/ui/Reusable/DataTable";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import NonSearchableSelectMenu from "@/app/components/ui/SelectMenus/NonSearchableSelectMenu";
import { useStaffProfileListAPI } from "@/app/hooks/api_hooks/configuration/useStaffProfileListAPI";

const STAFF_TYPE_FILTERS = [
  { id: "", title: "All types" },
  { id: "groomer", title: "Groomer" },
  { id: "doctor", title: "Doctor" },
  { id: "paravet", title: "Paravet" },
  { id: "clinic_manager", title: "Clinic Manager" },
  { id: "assistant_clinic_manager", title: "Assistant Clinic Manager" },
  { id: "housekeeping", title: "Housekeeping" },
  { id: "area_manager", title: "Area Manager" },
];

const STATUS_FILTERS = [
  { id: "", title: "All statuses" },
  { id: "ACTIVE", title: "Active" },
  { id: "INACTIVE", title: "Inactive" },
];

const searchInputClass =
  "h-11 w-full rounded-lg border border-gray-300 pl-9 pr-3 text-base text-slate-700 placeholder:text-gray-400 transition-colors focus:border-[#193A6A] focus:outline-none focus:ring-2 focus:ring-[#193A6A]/15 sm:h-[38px] sm:text-xs";

const StaffOnboardingList = () => {
  const navigate = useNavigate();
  const { getRouteKey } = useRouter();
  const { NO_APPOINTMENTS } = useImageConstants();
  const { execute, staffList, isLoading, hasError } = useStaffProfileListAPI();

  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [staffType, setStaffType] = useState("");
  const [status, setStatus] = useState("");

  const { register } = useForm<{ staff_type_filter: string; status_filter: string }>({ mode: "onChange" });

  useEffect(() => {
    const timer = setTimeout(() => setAppliedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    execute({ staffType, status, search: appliedSearch });
  }, [execute, staffType, status, appliedSearch]);

  const isFiltered = Boolean(appliedSearch || staffType || status);

  const columns = useMemo(
    () =>
      getStaffProfileColumns((staffId: string) =>
        navigate(`${getRouteKey("STAFF_ONBOARDING_FORM", "url")}?staff_id=${staffId}`),
      ),
    [navigate, getRouteKey],
  );

  return (
    <div className="px-5 py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-base font-semibold text-slate-900">Staff onboarding</h1>
          <p className="mt-1 text-xs text-[#6C727F]">
            {isLoading || hasError
              ? "Groomers, doctors, paravets and clinic staff."
              : `${staffList.length} staff member${staffList.length === 1 ? "" : "s"}${
                  isFiltered ? " matching these filters" : ""
                }.`}
          </p>
        </div>
        <SolidButton size="2XL" onButtonClick={() => navigate(getRouteKey("STAFF_ONBOARDING_FORM", "url"))}>
          Add staff
        </SolidButton>
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative w-full sm:w-[320px]">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search name, employee ID, phone or email"
            aria-label="Search staff"
            className={searchInputClass}
          />
        </div>

        <div className="w-full sm:w-56">
          <NonSearchableSelectMenu
            errorMessages={[{ message: "", type: "required" }]}
            fieldError={null}
            register={register("staff_type_filter")}
            selectItems={STAFF_TYPE_FILTERS}
            placeholder="All types"
            showTooltips={false}
            showDropdownIcon
            defaultSelected={STAFF_TYPE_FILTERS.find((type) => type.id === staffType)}
            onSelectItem={(item) => setStaffType(item?.id ?? "")}
            listBoxClassName="w-full"
            containerClassName="w-full"
          />
        </div>

        <div className="w-full sm:w-44">
          <NonSearchableSelectMenu
            errorMessages={[{ message: "", type: "required" }]}
            fieldError={null}
            register={register("status_filter")}
            selectItems={STATUS_FILTERS}
            placeholder="All statuses"
            showTooltips={false}
            showDropdownIcon
            defaultSelected={STATUS_FILTERS.find((item) => item.id === status)}
            onSelectItem={(item) => setStatus(item?.id ?? "")}
            listBoxClassName="w-full"
            containerClassName="w-full"
          />
        </div>
      </div>

      <div className="mt-4">
        {isLoading ? (
          <>
            <SkeletonType1 />
            <SkeletonType1 className="mt-4" />
          </>
        ) : hasError ? (
          <NoDataFoundComponent
            imageURL={NO_APPOINTMENTS}
            message="Staff list did not load"
            description="The request failed. Refresh the page to try again."
          />
        ) : staffList.length === 0 ? (
          <NoDataFoundComponent
            imageURL={NO_APPOINTMENTS}
            message={isFiltered ? "No staff match these filters" : "No staff onboarded yet"}
            description={
              isFiltered
                ? "Clear the search or filters to see everyone."
                : "Add your first staff member to start tracking their attendance."
            }
          />
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <DataTable data={staffList} columns={columns} maxHeight="600px" />
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffOnboardingList;
